import React, { useEffect } from "react";
// Swiper React components
import { Swiper, SwiperSlide } from "swiper/react";

// Swiper required modules
import { EffectCards, Autoplay } from "swiper/modules";

// Swiper styles
import "swiper/css";
import "swiper/css/effect-cards";

const ProjectModal = ({ project, onClose }) => {
  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden"; // Prevent background scrolling

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [onClose]);

  // Close modal when clicking on backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-x-hidden shadow-2xl">
        {/* Close Button */}
        <div className="sticky top-0 bg-white border-b border-gray-200 flex justify-end p-4 z-10">
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close modal"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 md:p-8">
          {/* Project Title */}
          <h2 className="text-[28px] font-bold text-[#377c7d] mb-6">
            {project.title}
          </h2>

          {/* Description */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Description
            </h3>
            <p className="text-gray-600 leading-relaxed text-[16px]">
              {project.description}
            </p>
          </div>

          {/* Technologies Used */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Technologies Used
            </h3>
            <div className="flex flex-wrap gap-3">
              {project.tech.map((tech, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-[#377c7d]/10 text-[#377c7d] rounded-full font-medium text-sm md:text-[16px]"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Images Section */}
          {project.images && project.images.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {project.videos && project.videos.length > 0
                  ? "Project Images"
                  : "Images"}
              </h3>
              <Swiper
                effect="cards"
                grabCursor={true}
                modules={[EffectCards, Autoplay]}
                className="rounded-lg overflow-hidden"
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
              >
                {project.images?.map((img, index) => (
                  <SwiperSlide key={index} className="no-shadow-slide">
                    <div className="flex items-center justify-center w-full sm:h-[430px] h-[220px]">
                      <img
                        src={img}
                        alt={`Project ${project.title}`}
                        className="rounded-lg object-center max-h-full "
                      />
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          )}

          {/* Videos Section (if any) */}
          {project.videos && project.videos.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Videos
              </h3>
              <div className="space-y-4">
                {project.videos.map((video, index) => (
                  <div key={index} className="relative w-full">
                    {video.includes("youtube.com") ||
                    video.includes("youtu.be") ? (
                      <iframe
                        className="w-full h-64 md:h-96 rounded-lg"
                        src={video
                          .replace("watch?v=", "embed/")
                          .replace("youtu.be/", "youtube.com/embed/")}
                        title={`${project.title} Video ${index + 1}`}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <video
                        className="w-full h-auto rounded-lg"
                        controls
                        src={video}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;